import { api } from "@zentrani/api-client";
import { isCapacitorNative, nativeHooks } from "./nativeBridge";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** True when the browser exposes the Web Push APIs. */
export function pushSupported(): boolean {
  // The Android app uses native push (FCM) instead of Web Push.
  if (isCapacitorNative()) return Boolean(nativeHooks.nativePush?.available);
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Rough iOS / iPadOS detection (includes desktop Safari on iPad). */
export function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ may report as Mac with touch.
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/** True when running as an installed PWA (Home Screen / standalone). */
export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  return window.matchMedia("(display-mode: standalone)").matches;
}

/**
 * iOS only delivers Web Push to an installed Home Screen PWA.
 * When true, the user must install before enabling push.
 */
export function iosNeedsInstall(): boolean {
  return isIosDevice() && !isStandalonePwa();
}

export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.register("/sw.js");
}

/** Register the push SW on app load (idempotent). */
export function ensurePushServiceWorkerRegistered(): void {
  if (typeof window === "undefined") return;
  if (isCapacitorNative()) return;
  if (!("serviceWorker" in navigator)) return;
  void navigator.serviceWorker.register("/sw.js").catch(() => {
    /* ignore registration failures in unsupported contexts */
  });
}

export async function hasActivePushSubscription(): Promise<boolean> {
  if (!pushSupported()) return false;
  if (nativeHooks.nativePush) return nativeHooks.nativePush.isRegistered();
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    return Boolean(sub);
  } catch {
    return false;
  }
}

export async function enablePushNotifications(): Promise<void> {
  if (nativeHooks.nativePush) {
    // Android app: native permission prompt + FCM token (src/native/push.ts).
    await nativeHooks.nativePush.enable();
    await api.me.updateNotificationSettings(true);
    return;
  }
  if (iosNeedsInstall()) {
    throw new Error(
      "En iPhone/iPad, primero añadí Zentrani a la pantalla de inicio y abrila desde el icono."
    );
  }
  if (!pushSupported()) {
    throw new Error("Este navegador no soporta notificaciones push.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Permiso de notificaciones denegado.");
  }
  const reg = await registerPushServiceWorker();
  if (!reg) throw new Error("No se pudo registrar el service worker.");

  const { public_key } = await api.me.vapidPublicKey();
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(public_key) as BufferSource,
  });
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error("Suscripción push incompleta.");
  }
  await api.me.pushSubscribe({
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
    user_agent: navigator.userAgent,
  });
  await api.me.updateNotificationSettings(true);
}

export async function disablePushNotifications(): Promise<void> {
  await api.me.updateNotificationSettings(false);
  if (nativeHooks.nativePush) {
    await nativeHooks.nativePush.disable();
    return;
  }
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    try {
      await api.me.pushUnsubscribe(sub.endpoint);
    } catch {
      /* ignore */
    }
    await sub.unsubscribe();
  }
}
